import {
  Autocomplete,
  AutocompleteChangeReason,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import {
  A360CommissionState,
  clearAllFilters,
  clearEmployerNamesFilter,
  clearStatementDateAndEmployerNameFilter,
  DEFAULT_GRID_PAGE_SIZE,
  DEFAULT_SELECTED_PAGE,
  DEFAULT_SORT_FIELD_ADVANCED_STATEMENTS,
  DEFAULT_SORT_FIELD_EARNED_STATEMENTS,
  DEFAULT_SORT_FIELD_EMPLOYER_SUMMARY,
  getAdjustmentDetails,
  getAdvancedStatements,
  getAvailableEmployerNames,
  getAvailableProducerCodes,
  getAvailableStatementDates,
  getCommissionPayable,
  getCommissionSummary,
  getEarnedStatements,
  getEmployerSummary,
  getPaymentReceipts,
  updateSelectedEmployerNames,
  updateSelectedProducerCodes,
  updateSelectedStatementDates,
} from 'Agent/Commissions/ducks/a360Commissions';
import {Async, isAsyncServerError, isLoaded} from 'Core/models/Async';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {A360CommissionSection} from '../CommissionSection';
import {A360CommissionsAlert} from '../Placeholders';
import {Producer} from '../types';

const isDataUnavailable = (
  availableProducerCodes: Async<Producer[]>,
  availableEmployerNames: Async<string[]>,
  availableStatementDates: Async<string[]>,
  selectedFilters: A360CommissionState['filter']
) => {
  const isError =
    isAsyncServerError(availableProducerCodes) ||
    isAsyncServerError(availableEmployerNames) ||
    isAsyncServerError(availableStatementDates);

  const filterDataUnavailable =
    (isLoaded(availableProducerCodes) && isEmptyArray(availableProducerCodes.Data)) ||
    (isLoaded(availableEmployerNames) && isEmptyArray(availableEmployerNames.Data)) ||
    (isLoaded(availableStatementDates) && isEmptyArray(availableStatementDates.Data));

  const filtersNotSelected =
    isEmptyArray(selectedFilters.selectedProducerCodes) ||
    isEmptyArray(selectedFilters.selectedStatementDates) ||
    isEmptyArray(selectedFilters.selectedEmployerNames);

  return isError || filterDataUnavailable || filtersNotSelected;
};

const isEmptyArray = (arr: any[]) => Array.isArray(arr) && arr.length === 0;

export const A360CommissionFilters = () => {
  const {
    availableProducerCodes,
    availableStatementDates,
    availableEmployerNames,
    filter,
    isInitialFilterLoading,
  } = useSelector((state: ReducerState) => state.a360Commissions);
  const {selectedAgency} = useSelector((state: ReducerState) => state.user.profile);
  const currentNewYorkStatus = pipe(
    selectedAgency,
    O.fold(
      () => false,
      agency => agency.isNewYork || false
    )
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAvailableProducerCodes(currentNewYorkStatus));
  }, [currentNewYorkStatus]);

  const producers = isLoaded(availableProducerCodes)
    ? availableProducerCodes?.Data?.map(({agentName, agentNumber}) => `${agentNumber} - ${agentName}`)
    : [];
  const statementDates = isLoaded(availableStatementDates) ? availableStatementDates?.Data : [];
  const employerNames = isLoaded(availableEmployerNames) ? availableEmployerNames?.Data : [];

  const handleProducerCodeChange = (values: string[]) => {
    dispatch(updateSelectedProducerCodes(values));
    dispatch(getAvailableStatementDates());
  };

  const handleStatementDateChange = (values: string[]) => {
    dispatch(updateSelectedStatementDates(values));
    dispatch(getAvailableEmployerNames());
  };

  const handleEmployerNameChange = (values: string[]) => {
    dispatch(updateSelectedEmployerNames(values));
  };

  const handleApplyFilter = () => {
    dispatch(getEmployerSummary(DEFAULT_SELECTED_PAGE, DEFAULT_GRID_PAGE_SIZE, [DEFAULT_SORT_FIELD_EMPLOYER_SUMMARY]));
    dispatch(
      getEarnedStatements(DEFAULT_SELECTED_PAGE, DEFAULT_GRID_PAGE_SIZE, [DEFAULT_SORT_FIELD_EARNED_STATEMENTS])
    );
    dispatch(
      getAdvancedStatements(DEFAULT_SELECTED_PAGE, DEFAULT_GRID_PAGE_SIZE, [DEFAULT_SORT_FIELD_ADVANCED_STATEMENTS])
    );
    dispatch(getCommissionPayable());
    dispatch(getPaymentReceipts());
    dispatch(getCommissionSummary());
    dispatch(getAdjustmentDetails());
  };

  const showNoInfoAvailableMsg = isDataUnavailable(
    availableProducerCodes,
    availableEmployerNames,
    availableStatementDates,
    filter
  );

  return (
    <div className="a360-commissions__filters">
      <A360CommissionSection className="a360-commissions__filters-container" aria-label="filters">
        <Typography className="a360-commissions__filters_label">Filter By:</Typography>
        <CommissionsFilter
          handleSelectionChange={handleProducerCodeChange}
          id="producer-codes-filter"
          isLoading={isInitialFilterLoading}
          filterKey="ProducerCode"
          label="Producer Codes"
          options={producers}
          producerFilterClearAction={clearAllFilters}
          value={filter.selectedProducerCodes}
        />
        <CommissionsFilter
          handleSelectionChange={handleStatementDateChange}
          id="statement-dates-filter"
          isLoading={isInitialFilterLoading || availableStatementDates?.key === 'Loading'}
          filterKey="StatementDate"
          label="Statement Dates"
          options={statementDates}
          value={filter.selectedStatementDates}
          limitTags={3}
        />
        <CommissionsFilter
          handleSelectionChange={handleEmployerNameChange}
          id="employer-names-filter"
          isLoading={isInitialFilterLoading || availableEmployerNames?.key === 'Loading'}
          filterKey="EmployerName"
          label="Employer Names"
          options={employerNames}
          value={filter.selectedEmployerNames}
        />
        <Button variant="contained" className="a360-commissions__filters_apply-filter-btn" onClick={handleApplyFilter}>
          Apply Filter
        </Button>
      </A360CommissionSection>

      {showNoInfoAvailableMsg && !isInitialFilterLoading && (
        <div className="a360-commissions__filters-not_available">
          <A360CommissionsAlert message="No Information Available." />
        </div>
      )}
    </div>
  );
};

type CommissionFilterKey = 'ProducerCode' | 'StatementDate' | 'EmployerName';

type CommissionFilterProps = {
  handleSelectionChange: (value: string[]) => void;
  id: string;
  label: string;
  limitTags?: number;
  filterKey: CommissionFilterKey;
  producerFilterClearAction?: (...args: any[]) => any;
  options: string[];
  value: string[];
  isLoading: boolean;
};

const SELECT_ALL_OPTION_VALUE = 'Select All';

export const CommissionsFilter = (props: CommissionFilterProps) => {
  const {
    handleSelectionChange,
    id,
    isLoading,
    label,
    limitTags = 2,
    options,
    producerFilterClearAction,
    value,
    filterKey,
  } = props;
  const dispatch = useDispatch();

  const optionsWithSelectAll = options.length > 0 ? [SELECT_ALL_OPTION_VALUE, ...options] : [];
  const areAllOptionsSelected = value.length === options.length && value.length !== 0;

  // Clears all selected options from the filter text field
  const handleClear = () => {
    switch (filterKey) {
      case 'ProducerCode':
        dispatch(producerFilterClearAction?.());
        break;
      case 'StatementDate':
        dispatch(clearStatementDateAndEmployerNameFilter());
        break;
      case 'EmployerName':
        dispatch(clearEmployerNamesFilter());
        break;
      default:
        return;
    }
  };

  const handleChange = (_event: React.SyntheticEvent, value: string[], reason: AutocompleteChangeReason) => {
    if (reason === 'clear' || value.length === 0) {
      return handleClear();
    }
    // If Select All option is selected / unselected
    if (value.includes(SELECT_ALL_OPTION_VALUE)) {
      if (areAllOptionsSelected) {
        handleClear();
      } else {
        handleSelectionChange(options);
      }
    } else {
      handleSelectionChange(value);
    }
  };

  return (
    <Autocomplete
      className="a360-commissions__filters_filter-input"
      multiple
      id={id}
      loading={isLoading}
      options={optionsWithSelectAll}
      disableCloseOnSelect
      limitTags={limitTags}
      value={value}
      getOptionLabel={option => (option === SELECT_ALL_OPTION_VALUE ? SELECT_ALL_OPTION_VALUE : option)}
      renderTags={values => <RenderSelectedFilterOptions values={values} limit={limitTags} />}
      renderOption={(props, option) => {
        return (
          <li key={option} {...props}>
            <Checkbox
              style={{marginRight: 8}}
              checked={option === SELECT_ALL_OPTION_VALUE ? areAllOptionsSelected : value.includes(option)}
              indeterminate={option === SELECT_ALL_OPTION_VALUE && value.length > 0 && !areAllOptionsSelected}
            />
            {option === SELECT_ALL_OPTION_VALUE ? SELECT_ALL_OPTION_VALUE : option}
          </li>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={value.length > 0 ? undefined : label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <div className="a360-commissions__filters_filter-input-adornment">
                {isLoading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </div>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            'aria-label': label,
          }}
        />
      )}
      disablePortal={false}
      onChange={handleChange}
    />
  );
};

const RenderSelectedFilterOptions = ({values, limit}: {values: string[]; limit: number}) => {
  return (
    <>
      {values.slice(0, limit).map(v => (
        <Chip label={v} sx={{margin: 'auto 0.25rem'}} size="small" key={v} />
      ))}
      {values.length > limit ? `+${values.slice(limit).length}` : null}
    </>
  );
};
